import '@/types/gtag.d';

export class AnalyticsProvider {
  private static instance: AnalyticsProvider;
  private measurementId = '';

  public static getInstance(): AnalyticsProvider {
    if (!AnalyticsProvider.instance) {
      AnalyticsProvider.instance = new AnalyticsProvider();
    }
    return AnalyticsProvider.instance;
  }

  public configure(args: { measurementId: string }): void {
    const { measurementId } = args;
    this.measurementId = measurementId;
  }

  private shouldTrack(): boolean {
    // Skip tracking in dev mode unless debug is enabled
    if (import.meta.env.DEV && import.meta.env.VITE_GA_DEBUG !== 'true') {
      return false;
    }
    return true;
  }

  private callGtag(
    command: string,
    targetOrEventName: string,
    params?: Record<string, unknown>,
  ): void {
    if (!this.shouldTrack()) {
      return;
    }

    if (typeof window.gtag !== 'function') {
      // gtag not loaded (blocked by ad blocker or not in production)
      return;
    }

    window.gtag(command, targetOrEventName, params);
  }

  public trackPageView(args: {
    path: string;
    title: string;
    userId?: string | null;
  }): void {
    const { path, title, userId } = args;

    const params: Record<string, unknown> = {
      page_path: path,
      page_title: title,
      page_location: window.location.href,
    };

    if (userId) {
      params.user_id = userId;
    }

    this.callGtag('event', 'page_view', params);
  }

  public trackEvent(args: {
    eventName: string;
    eventParams: Record<string, unknown>;
    userId?: string | null;
  }): void {
    const { eventName, eventParams, userId } = args;

    const params: Record<string, unknown> = {
      ...eventParams,
    };

    if (userId) {
      params.user_id = userId;
    }

    this.callGtag('event', eventName, params);
  }

  public setUserId(args: { userId: string | null }): void {
    const { userId } = args;

    this.callGtag('config', this.measurementId, {
      user_id: userId ?? undefined,
    });
  }
}

export const configureAnalytics = (args: { measurementId: string }): void => {
  AnalyticsProvider.getInstance().configure(args);
};

export const getAnalytics = (): AnalyticsProvider => {
  return AnalyticsProvider.getInstance();
};
